import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type { InvoiceInput } from '@/lib/api/domains/invoices';
import type { InvoiceId } from '@/lib/api/ids';
import { keys } from '@/lib/query-keys';

/*
 * Mutations (spec §7.4). Pessimistic: mutate → invalidate. NO optimistic
 * updates on financial mutations. Create/update/void each send an idempotency
 * key (retry is off, so the key is the dedupe mechanism). On success the
 * key-factory prefix cascade invalidates lists + the affected detail in one
 * call.
 */

function idempotencyKey(): string {
  return crypto.randomUUID();
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InvoiceInput) =>
      api.invoices.create(input, idempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.invoices.lists() });
    },
  });
}

export function useUpdateInvoice(invoiceId: InvoiceId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InvoiceInput) =>
      api.invoices.update(invoiceId, input, idempotencyKey()),
    onSuccess: () => {
      // Prefix cascade: reaches lists and this detail.
      void queryClient.invalidateQueries({ queryKey: keys.invoices.all() });
    },
  });
}

export function useVoidInvoice(invoiceId: InvoiceId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.invoices.void(invoiceId, idempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.invoices.all() });
    },
  });
}
